import { IconAttention2, IconPlus } from "@studyplus/boron-ui";
import classNames from "classnames";
import { format } from "date-fns";
import sum from "lodash-es/sum";
import * as React from "react";
import { Link } from "react-router-dom";
import Icon from "../../components/atoms/Icon/index";
import Loader from "../../components/atoms/Loader";
import Time from "../../components/atoms/Time";
import TimelineItem from "../../components/features/Timeline/TimelineItem";
import { useDrawerContext } from "../../components/general/Drawer/useDrawerContext";
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverProvider,
  PopoverTrigger,
} from "../../components/general/Popover/Popover";
import { Progress } from "../../domains/DrillProgress";
import DeviceHelper from "../../helpers/DeviceHelper";
import { toTimeString, truncateMinutes } from "../../helpers/TimeHelper";
import { useFetchTimelineDrillProgress } from "../../hooks/http/useFetchTimelineDrillProgress";
import { useQueryError } from "../../hooks/http/useQueryError";
import { useEffectAvoidFirst } from "../../hooks/useEffectAvoidFirst";
import OperatorInterface from "../../interfaces/OperatorInterface";
import StudentInterface from "../../interfaces/StudentInterface";
import { ABTestSwitcher } from "../../lib/tools/ABTestSwitcher";
import { CommentFromStudent } from "./CommentFromStudent";
import { KoekakeAssists } from "./KoekakeAssists";
import {
  MergedTimelineEntry,
  mergeTimelineEntries,
} from "./mergeTimelineEntries";
import styles from "./StudentTimelineSection.scss";
import { StudyRecordItem } from "./StudyRecordItem";
import {
  TimelineStudyRecord,
  useFetchStudentDailyStudyRecords,
} from "./useFetchStudentDailyStudyRecords";
import { TimelineStudent } from "./useFetchTimelineStudents";

interface Props {
  currentOperator: OperatorInterface;
  // 生徒一覧 API（timeline/students）の 1 生徒
  student: TimelineStudent;
  sectionId: string;
  // 表示対象の日付（yyyy-MM-dd）。生徒単位 API は date 必須のため、当日もページ側で明示する
  date: string;
  abTestCodes: string[] | undefined;
}

// サマリータブの生徒単位の表示ブロック。
// ヘッダー行（アバター + 生徒名 + Total）+ 声かけアシスト + タイムラインカード列で構成する。
// AI 質問統合表示の凍結（005-freeze-ai-question-timeline）に伴い、学習記録のみを扱う。
const StudentTimelineSection = (props: Props) => {
  const {
    studyRecords,
    updateStudyRecordQueryCache,
    error,
    isLoading: isStudyRecordsLoading,
  } = useFetchStudentDailyStudyRecords({
    studentId: props.student.id,
    date: props.date,
    enabled: true,
  });
  useQueryError(error);

  const records = studyRecords ?? [];

  // 学習記録を時刻昇順に並べる
  const entries = React.useMemo(
    () => mergeTimelineEntries(studyRecords ?? []),
    [studyRecords],
  );

  // 学習定着バーの情報を生徒ごとに取得（Notee 教材は通常教材扱いのため除外）
  const drillStudyRecordIds = records
    .filter(
      (record) =>
        record.learningMaterial?.isTypeDrill && !record.learningMaterial?.notee,
    )
    .map((record) => record.id);
  const { data: drillProgressMap } = useFetchTimelineDrillProgress({
    sectionId: props.sectionId,
    studyRecordIds: drillStudyRecordIds,
    enabled: drillStudyRecordIds.length > 0,
    studentId: props.student.id,
  });

  // 生徒パス API（study_records）は生徒メタを含まないため、
  // カード内の実需（いいね API 用の id・showHeader 時の名前表示）に絞った最小の生徒情報を
  // 生徒一覧 API の値から合成する
  const studentForCards = React.useMemo(
    () =>
      ({
        id: props.student.id,
        fullName: props.student.name,
      }) as unknown as StudentInterface,
    [props.student.id, props.student.name],
  );

  return (
    <div
      className={styles.root}
      data-testid={`${props.student.id}-timeline-summary-item`}
    >
      <Header student={props.student}>
        {/* 内容が揃うまでは Total を出さない（Total が一瞬 0 で出るのを防ぐ） */}
        {!isStudyRecordsLoading && (
          <div className={styles.metrics}>
            <Total
              studentId={props.student.id}
              studyRecords={records}
              hasLearningManagementService={
                props.student.hasLearningManagementService
              }
            />
          </div>
        )}
      </Header>
      {isStudyRecordsLoading ? (
        <Loader loading />
      ) : (
        <>
          <ABTestSwitcher
            codes={props.abTestCodes}
            components={{
              "tokyo_startup_pjt/koekake_assist": (
                <KoekakeAssists studentId={props.student.id} />
              ),
            }}
            defaultComponent={<></>} // NOTE: デフォルトで何も表示させたくないのでフラグメントを渡す
          />
          <TimelineItemsSwitcher
            {...props}
            entries={entries}
            studyRecords={records}
            studentForCards={studentForCards}
            drillProgressMap={drillProgressMap}
            updateStudyRecordQueryCache={updateStudyRecordQueryCache}
          />
        </>
      )}
    </div>
  );
};

const Header = ({
  student,
  children,
}: React.PropsWithChildren<{ student: TimelineStudent }>) => {
  return (
    <div className={styles.header}>
      {/* アイコンと名前は常に同じ段に並べる（長い名前は名前側だけが折り返す）。
          Total（children）は狭い画面で次の段へ折り返す */}
      <div className={styles.identity}>
        <Icon name="icon-navi-avatar" className={styles.icon} />
        <div className={`${styles.name} gap-3`}>
          <Link to={`/students/${student.id}/timeline`}>{student.name}</Link>
        </div>
      </div>
      {children}
    </div>
  );
};

// ドロワーヘッダーの日時表示（学習記録ドロワー・非リンク）
const DrawerHeaderTime = ({ dateTime }: { dateTime: string }) => {
  return (
    <span className="ml-6 text-[1.4rem] font-bold text-gray-800">
      {format(new Date(dateTime), "yyyy/M/d H:mm")}
    </span>
  );
};

type TotalProps = {
  studentId: string;
  studyRecords: TimelineStudyRecord[];
  // 学習管理サービス（learning_management）契約の有無（生徒一覧 API 由来）
  hasLearningManagementService: boolean;
};

const Total = ({
  studentId,
  studyRecords,
  hasLearningManagementService,
}: TotalProps) => {
  const totalSeconds = sum(
    studyRecords.map((record) => record.numberOfSeconds),
  );

  // 学習管理サービス未契約の生徒は学習記録が閲覧制限され Total が不完全なため、
  // 「＋」「！」アイコンを付し、ホバーで学習管理サービスの訴求ポップアップを表示する
  if (!hasLearningManagementService) {
    return (
      <PopoverProvider>
        <Popover>
          <PopoverTrigger
            className="ml-10 cursor-pointer text-black"
            data-testid={`${studentId}-free-plan-total-trigger`}
          >
            Total:{" "}
            <span className="inline-flex items-center whitespace-nowrap">
              <Time seconds={totalSeconds} />
              <IconPlus className="h-3 w-3" />
              <IconAttention2 className="h-2 w-2 text-gray-900" />
            </span>
          </PopoverTrigger>
          <PopoverPortal>
            <PopoverContent>
              <div className="text-center text-sm font-normal">
                学習管理サービスを追加すると生徒が記録した
                <br />
                全学習記録が閲覧できます
              </div>
            </PopoverContent>
          </PopoverPortal>
        </Popover>
      </PopoverProvider>
    );
  }

  return (
    // 狭い画面（〜480px未満）ではラベル「Total:」と値を縦2段に積んで横幅を抑え、名前と同じ行に
    // 収める。480px 以上（sm:）では従来どおり横並び（Total: X時間Y分）。値は常に内部で割らない。
    <div className="ml-10">
      Total:{" "}
      <span className="block whitespace-nowrap sm:inline">
        <Time seconds={totalSeconds} />
      </span>
    </div>
  );
};

type ItemsProps = Props & {
  // 時刻昇順のマージ済みエントリ（学習記録のみ）
  entries: MergedTimelineEntry[];
  studyRecords: TimelineStudyRecord[];
  // カードに渡す最小の生徒情報（生徒パス API は生徒メタを含まないため合成済み）
  studentForCards: StudentInterface;
  drillProgressMap?: Map<string, Progress>;
  updateStudyRecordQueryCache: ReturnType<
    typeof useFetchStudentDailyStudyRecords
  >["updateStudyRecordQueryCache"];
};

// エントリを渡された順序のまま縦に並べる（モバイル・ドロワー用）
const TimelineItemsForDrawerAndMobile = ({
  currentOperator,
  entries,
  drillProgressMap,
  ...props
}: ItemsProps) => {
  const lastItemIndex = entries.length - 1;
  return (
    <>
      {entries.map((entry, index) => {
        // アイテム間の区切りは「各アイテムの上線（border-t）」で表現し、先頭（index 0）には出さない。
        // 本レンダラは狭い画面（縦並び）専用。
        // NOTE: 本プロジェクトは Tailwind preflight 無効（tailwind.config.js）のため、border-solid だけだと
        // 4 辺すべてが既定幅(medium)で表示されてしまう。先に border-0 で全辺 0 にしてから border-t で
        // 上辺だけ 1px にする（リポジトリ既存の単辺ボーダーと同じ書き方）。
        const dividerClass =
          index === 0
            ? ""
            : "border-0 border-t border-[color:var(--bui-colors-gray-300)] border-solid";
        return (
          // TimelineItem をラップすると自身の .root:last-child が効いて border-bottom が 0 になり、
          // 上線（dividerClass）と二重にならない。
          <div key={entry.studyRecord.id} className={dividerClass}>
            <TimelineItem
              currentOperator={currentOperator}
              studyRecord={entry.studyRecord}
              student={props.studentForCards}
              sectionId={props.sectionId}
              canSendComment={props.student.canSendComment}
              updateStudyRecordQueryCache={props.updateStudyRecordQueryCache}
              isLastItem={index === lastItemIndex}
              drillProgressMap={drillProgressMap}
              isShowDrillAnswer
            />
          </div>
        );
      })}
    </>
  );
};

// タブレット幅以上の横スクロールカード列（時刻昇順・時間帯ラベル付き）
const TimelineItems = (props: ItemsProps) => {
  const {
    openDrawer: _openDrawer,
    setHeaderComponent,
    setBodyComponent,
  } = useDrawerContext();
  const currentFocusStudyRecordIdRef = React.useRef<string | undefined>(
    undefined,
  );

  const openDrawer = (studyRecordId?: string) => () => {
    currentFocusStudyRecordIdRef.current = studyRecordId;
    const studyRecord = props.studyRecords.find(
      (record) => record.id === studyRecordId,
    );
    setHeaderComponent(
      <Header student={props.student}>
        {studyRecord && <DrawerHeaderTime dateTime={studyRecord.recordedAt} />}
      </Header>,
    );
    setBodyComponent(
      <DrawerBody focusStudyRecordId={studyRecordId} {...props} />,
    );
    _openDrawer();
  };

  // setBodyComponent は開いた瞬間の props を焼き付けた React 要素を state に保持する。
  // ドロワー内でいいね/コメントによりキャッシュが更新されて studyRecords が新しくなっても、
  // 焼き付けられたスナップショットは古いままとなり、サマリー側のカードと表示が同期しない
  // （さらに古い配列を土台に上書きが走り、直前の追加分がロストする）。studyRecords 変化時に
  // ドロワー本体を最新 props で作り直して同期を保つ (WEB-2192)。
  useEffectAvoidFirst(() => {
    setBodyComponent(
      <DrawerBody
        focusStudyRecordId={currentFocusStudyRecordIdRef.current}
        {...props}
      />,
    );
  }, [props.studyRecords]);

  return (
    // NOTE: タブレット幅で横スクロールさせるために、MainLayoutのmin-widthにautoが指定されないようにする
    <div data-main-layout_min-width-auto_tablet="false">
      <div className="flex overflow-x-auto p-4">
        {props.entries.map((entry, i) => {
          const recordedHour = toTimeString(truncateMinutes(entry.time));
          const prevRecordedHour =
            i > 0
              ? toTimeString(truncateMinutes(props.entries[i - 1].time))
              : "";

          const isBreakRecordedHour = recordedHour !== prevRecordedHour;

          return (
            <div
              key={entry.studyRecord.id}
              className={classNames({ "ml-2": isBreakRecordedHour })}
            >
              <RecordedHour
                isBreakRecordedHour={isBreakRecordedHour}
                recordedHour={recordedHour}
              />
              <div
                className="group flex cursor-pointer"
                onClick={openDrawer(entry.studyRecord.id)}
              >
                <StudyRecordItem
                  currentOperator={props.currentOperator}
                  studyRecord={entry.studyRecord}
                  student={props.studentForCards}
                  sectionId={props.sectionId}
                  canSendComment={props.student.canSendComment}
                  updateStudyRecordQueryCache={
                    props.updateStudyRecordQueryCache
                  }
                  drillProgress={props.drillProgressMap?.get(
                    entry.studyRecord.id,
                  )}
                />
                <CommentFromStudent studyRecord={entry.studyRecord} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const RecordedHour = ({
  isBreakRecordedHour,
  recordedHour,
}: {
  isBreakRecordedHour: boolean;
  recordedHour: string;
}) => {
  // 要素の高さを合わせたいのでinvisibleクラスを使用して出しわける
  return (
    <span className={classNames("ml-2", { invisible: !isBreakRecordedHour })}>
      {recordedHour}
    </span>
  );
};

type DrawerBodyProps = ItemsProps & {
  focusStudyRecordId?: string;
};
// 学習記録ドロワーの中身
const DrawerBody = (props: DrawerBodyProps) => {
  const focusStudyRecordIdRef = React.useRef<string | undefined>(
    props.focusStudyRecordId,
  );

  const entriesToDisplay = React.useMemo<MergedTimelineEntry[]>(() => {
    // props.entries は時刻昇順のマージ済み。ドロワーは降順で表示するため reverse する
    const descending = [...props.entries].reverse();
    return focusStudyRecordIdRef.current
      ? descending.filter(
          (entry) => entry.studyRecord.id === focusStudyRecordIdRef.current,
        )
      : descending;
  }, [props.entries]);

  return (
    <div className="max-w-[92rem]">
      <TimelineItemsForDrawerAndMobile {...props} entries={entriesToDisplay} />
    </div>
  );
};

const TimelineItemsSwitcher = (props: ItemsProps) => {
  // モバイルは新しい順（降順）で縦に並べる
  const descendingEntries = React.useMemo(
    () => [...props.entries].reverse(),
    [props.entries],
  );

  if (DeviceHelper.isScreenTablet()) {
    return <TimelineItems {...props} />;
  } else {
    return (
      <TimelineItemsForDrawerAndMobile {...props} entries={descendingEntries} />
    );
  }
};

export default StudentTimelineSection;
